import React from 'react'
import { Routes, Route, useParams } from 'react-router-dom'
import { Market } from 'api'
import { MID_TRADE_VIEW_HISTORICAL, MID_TRADE_VIEW_INCOMING, MID_TRADE_VIEW_INITIATED } from 'selectors/midTradeEventsSelectors'
import { AuthContent } from './login/AuthContent'
import { EnvironmentProvider } from 'view/shared/environment'
import { StagingScreen } from 'view/routes/staging'
import { IncomingNegotiationsScreen } from 'view/routes/requests/incoming'
import { InitiatedNegotiationsScreen } from 'view/routes/requests/initiated'
import RFQTicket from 'view/routes/requests/rfqTicket'
import { DealsScreen } from 'view/routes/deals'
import DealTicket from 'view/routes/deals/components/DealTicket'
import { CollateralBasketAllocationDialog, FundAssignmentDialog } from 'view/shared/allocationDialogs'
import { MidTradeEventsScreen } from 'view/routes/midTradeEvents'
import { OpenReposScreen } from 'view/routes/openRepos'
import { StructuredScreen } from 'view/routes/structured'
import { CollateralBasketScreen } from 'view/routes/collateralBasket'
import { AuditScreen } from 'view/routes/audit'
import { CompletedNegotiationsScreen } from 'view/routes/requests/completed'
import { TradingEntityDefaultsScreen } from 'view/routes/tradingEntityDefaults'
import { SalesCoverageScreen } from 'view/routes/salesCoverage'
import { TradeSplitScreen } from 'view/routes/tradeSplit'
import { HaircutScreen } from 'view/routes/haircut'
import { TradingControlScreen } from 'view/routes/admin/tradingControl'
import { PlatformScreen } from 'view/routes/admin/platform'
import { OrderRecordKeepingScreen } from 'view/routes/admin/orderRecordKeeping'
import { OrganisationScreen } from 'view/routes/admin/organisation'
import { TradingEntityScreen } from 'view/routes/admin/tradingEntity'
import { ClearingHouseScreen } from 'view/routes/admin/clearingHouse'
import { UsersScreen } from 'view/routes/admin/users'
import { CollateralScreen } from 'view/routes/admin/collateral'
import { AdminCollateralBasketScreen } from 'view/routes/admin/collateralBasket'
import { FatFingerScreen } from 'view/routes/admin/fatFinger'
import { HolidayScreen } from 'view/routes/admin/holiday'
import { IndexScreen } from 'view/routes/admin/index'
import { ProductScreen } from 'view/routes/admin/product'
import { SectorScreen } from 'view/routes/admin/sector'
import { FxRateScreen } from 'view/routes/admin/fxRate'
import { ActivityMonitor } from 'view/shared/activityMonitor'
import { GrowlList } from './growl/GrowlList'
import { CurveNegotiationsScreen } from 'view/routes/requests/incoming/curves/CurveNegotiationsScreen'
import { ClientLogStreamScreen } from 'view/routes/admin/clientLogStream'
import { RollsDealsScreen } from 'view/routes/deals/RollsDealsScreen'
import { AxeBoardInitiator, AxeBoardResponder } from 'view/routes/axeBoard'
import { TradingEntityAliasesScreen } from 'view/routes/tradingEntityAliases'
import { PrimeBrokerDefaultsScreen } from 'view/routes/primeBrokerDefaults'
import { TradingLimitsScreen } from 'view/routes/tradingLimits'
import { RFQTicketOpenFinProps } from 'view/routes/requests/rfqTicket/RFQTicketOpenFin'
import { IssuerOrganisationsScreen } from 'view/routes/admin/issuerOrganisations'
import { ResponderSalesCoverageScreen } from 'view/routes/responderSalesCoverage'
import { SectorMappingScreen } from 'view/routes/admin/sector/SectorMappingScreen'
import { InitiatedGridViewScreen } from 'view/routes/requests/initiatedGridView'
import { TradingFatFingerLimitsScreen } from 'view/routes/tradingFatFingerLimits'

export const Tearout: React.FC = () => (
  <AuthContent isMainWindow={false}>
    <EnvironmentProvider isTearout={true}>
      <Routes>
        <Route path="/staging" element={<StagingScreen />} />
        <Route path="/rfqs/initiated" element={<InitiatedNegotiationsScreen />} />
        <Route path="/initiatedGridView" element={<InitiatedGridViewScreen />} />
        <Route path="/rfqs/:negotiationId/initiatorTicket" element={<RFQTicketWrapper ownOrder={true} />} />
        <Route path="/rfqs/incoming" element={<IncomingNegotiationsScreen />} />
        <Route path="/rfqs/curves" element={<CurveNegotiationsScreen />} />
        <Route path="/rfqs/:negotiationId/responderTicket" element={<RFQTicketWrapper ownOrder={false} />} />
        <Route path="/rfqs/:negotiationId/historyTicket" element={<RFQTicketWrapper history={true} ownOrder={false} />} />
        <Route path="/trades" element={<DealsScreen />} />
        <Route path="/trades/:dealId/ticket" element={<DealTicketWrapper />} />
        <Route path="/allocationDialog/:dealId" element={<CollateralBasketAllocationDialogWrapper />} />
        <Route path="/assignmentDialog/:dealId" element={<FundAssignmentDialogWrapper />} />
        <Route path="/midTradeEvents" element={<MidTradeEventsScreen />} />
        <Route path={`/midTradeEvents/${MID_TRADE_VIEW_INITIATED}`} element={<MidTradeEventsScreen view={MID_TRADE_VIEW_INITIATED} />} />
        <Route path={`/midTradeEvents/${MID_TRADE_VIEW_INCOMING}`} element={<MidTradeEventsScreen view={MID_TRADE_VIEW_INCOMING} />} />
        <Route path={`/midTradeEvents/${MID_TRADE_VIEW_HISTORICAL}`} element={<MidTradeEventsScreen view={MID_TRADE_VIEW_HISTORICAL} />} />
        <Route path="/openRepos" element={<OpenReposScreen />} />
        <Route path="/structured" element={<StructuredScreen />} />
        <Route path="/baskets" element={<CollateralBasketScreen />} />
        <Route path="/history" element={<AuditScreen />} />
        <Route path="/rfqs/historical" element={<CompletedNegotiationsScreen />} />
        <Route path="/tradingEntityDefaults" element={<TradingEntityDefaultsScreen />} />
        <Route path="/primeBrokerDefaults" element={<PrimeBrokerDefaultsScreen />} />
        <Route path="/salesCoverage" element={<SalesCoverageScreen />} />
        <Route path="/responderSalesCoverage" element={<ResponderSalesCoverageScreen />} />
        <Route path="/tradingLimits" element={<TradingLimitsScreen />} />
        <Route path="/tradingEntityAliases" element={<TradingEntityAliasesScreen />} />
        <Route path="/tradeSplit" element={<TradeSplitScreen />} />
        <Route path="/haircut" element={<HaircutScreen />} />
        <Route path="/admin/tradingControl" element={<TradingControlScreen />} />
        <Route path="/admin/platform" element={<PlatformScreen />} />
        <Route path="/admin/platform/clientLogging" element={<ClientLogStreamScreen />} />
        <Route path="/admin/orderRecordKeeping" element={<OrderRecordKeepingScreen />} />
        <Route path="/admin/organisations" element={<OrganisationScreen />} />
        <Route path="/admin/entities" element={<TradingEntityScreen />} />
        <Route path="/admin/clearingHouses" element={<ClearingHouseScreen />} />
        <Route path="/admin/users" element={<UsersScreen />} />
        <Route path="/admin/collaterals" element={<CollateralScreen />} />
        <Route path={`/admin/collaterals/${Market.EUROPE}`} element={<CollateralScreen market={Market.EUROPE} />} />
        <Route path={`/admin/collaterals/${Market.US}`} element={<CollateralScreen market={Market.US} />} />
        <Route path="/admin/baskets" element={<AdminCollateralBasketScreen />} />
        <Route path="/admin/issuerOrganisations" element={<IssuerOrganisationsScreen />} />
        <Route path="/admin/fatFingers" element={<FatFingerScreen />} />
        <Route path="/tradingFatFingerLimits" element={<TradingFatFingerLimitsScreen />} />
        <Route path="/admin/holidays" element={<HolidayScreen />} />
        <Route path="/admin/indices" element={<IndexScreen />} />
        <Route path="/admin/products" element={<ProductScreen />} />
        <Route path="/admin/sectors" element={<SectorScreen />} />
        <Route path="/admin/sectors/mapping" element={<SectorMappingScreen />} />
        <Route path="/admin/fxRates" element={<FxRateScreen />} />
        <Route path="/activitymonitor" element={<ActivityMonitor />} />
        <Route path="/rolls" element={<RollsDealsScreen />} />
        <Route path="/axeBoardInitiator" element={<AxeBoardInitiator />} />
        <Route path="/axeBoardResponder" element={<AxeBoardResponder />} />
      </Routes>
      <GrowlList />
    </EnvironmentProvider>
  </AuthContent>
)

const RFQTicketWrapper = (props: Omit<RFQTicketOpenFinProps, 'negotiationId'>) => {
  const { negotiationId } = useParams()
  return <RFQTicket negotiationId={negotiationId} {...props} />
}

const DealTicketWrapper = () => {
  const { dealId } = useParams()
  return <DealTicket dealId={dealId} />
}

const CollateralBasketAllocationDialogWrapper = () => {
  const { dealId = '' } = useParams()
  return <CollateralBasketAllocationDialog dealId={dealId} />
}

const FundAssignmentDialogWrapper = () => {
  const { dealId = '' } = useParams()
  return <FundAssignmentDialog dealId={dealId} />
}
